import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CLEARANCE as CLEARANCE_UNITS, BRAND_LOGO, STAR_NOTE_ALL } from '../data/clearance';
import { PRICES, STOCK } from '../data/pricing';
import { addToCart } from '../lib/cart';
import { Tag, AlertCircle, Phone, CheckCircle, Clock, Calendar, ShieldCheck, ListChecks, Thermometer } from 'lucide-react';
import { track, FUNNEL } from '../lib/track';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// The WhatsApp logo, the same one as the floating WhatsApp button.
const WhatsAppGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={`shrink-0 ${className || ''}`}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export const ClearancePage = () => {
  const [brand, setBrand] = useState('All');
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Clearance Sale | Sealed-Pack Daikin, O-General & Hitachi ACs | Citi Cool';
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Brand new, sealed-pack Daikin, O-General and Hitachi ACs at clearance prices, GST included, with warranty as per OEM standards for the product type. Delivery across Delhi NCR.');
  }, []);

  const clearanceReasons = [
    { title: 'Cancelled Orders', desc: 'Brand new, completely sealed units from bulk corporate orders that were altered or cancelled.' },
    { title: 'Previous Season', desc: '100% unused, sealed stock from earlier years\' manufacturing. Each unit shows its model year.' },
    { title: 'Surplus Inventory', desc: 'Excess sealed units cleared from our warehouse to make room for new incoming models.' },
    { title: 'OEM Warranty', desc: 'Warranty as per OEM standards for the product type.' }
  ];

  // The units live in data/clearance.js and their price and stock in
  // data/pricing.js (PRICES and STOCK, same id), shared with each unit's own
  // page, the cart, the payment server and the Google Merchant feed. Stock
  // comes off by itself when a unit sells online. 0 = SOLD OUT: the card stays,
  // last in the list, with the red "Sold out" pill.
  const CLEARANCE = CLEARANCE_UNITS
    .map((u) => {
      const qty = (STOCK[u.id] || {})[u.kit] || 0;
      return {
        ...u,
        title: u.displayName,
        model: u.code,
        price: (PRICES[u.id] || {})[u.kit],
        qty,
        sold: qty <= 0,
        hasKit: u.kit === 'withKit',
      };
    })
    .filter((u) => typeof u.price === 'number')
    .sort((a, b) => Number(a.sold) - Number(b.sold));   // stable: sold-out last
  const BRANDS = ['All', ...Array.from(new Set(CLEARANCE.map((u) => u.brand)))];
  const shown = brand === 'All' ? CLEARANCE : CLEARANCE.filter((u) => u.brand === brand);
  const similarLink = (u) => `https://wa.me/917703900307?text=${encodeURIComponent(`Hi, the ${u.brand} ${u.name} (${u.model}) on your clearance page is sold out. Do you have anything similar?`)}`;
  const buyNow = (u) => {
    addToCart(u.id, u.kit, 1);
    track(FUNNEL.ADD_TO_CART, { model: u.id, kit: u.kit, qty: 1, value: u.price, source: 'clearance' });
    track(FUNNEL.CHECKOUT_STARTED, { value: u.price, units: 1, source: 'clearance_buy_now' });
    navigate('/checkout');
  };


  return (
    <div className="pt-16 lg:pt-20 pb-0 bg-gray-50 min-h-screen">
      
      {/* ─── HERO SECTION ─── */}
      <section className="relative flex items-center min-h-[auto] lg:min-h-[85vh] bg-slate-950 overflow-hidden">
        
        {/* Background Image & Overlays */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/clearance-banner.webp" 
            alt="Citi Cool Premium AC Clearance Vault"
            fetchpriority="high"
            className="w-full h-full object-cover object-[70%_center] lg:object-right opacity-60 mix-blend-luminosity"
          />
          {/* Split gradient matching VRV Page */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/95 via-slate-950/80 to-slate-950/95 lg:bg-gradient-to-r lg:from-slate-950/95 lg:via-slate-900/80 lg:to-transparent"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            
            {/* Left: Messaging */}
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-3 py-1.5 lg:px-4 lg:py-2 text-[11px] lg:text-sm font-semibold text-blue-100 mb-4 lg:mb-6 shadow-lg">
                <Tag size={14} className="text-blue-300" />
                Sealed Pack Clearance Sale
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white leading-tight mb-3 lg:mb-5">
                Looking for a Second Hand AC?
                <span className="block text-blue-300 mt-1">Stop. Read This First.</span>
              </h1>

              <p className="text-sm lg:text-lg text-white/90 leading-snug lg:leading-relaxed mb-6 lg:mb-8 max-w-lg font-medium">
                Why risk buying a second-hand AC with a hidden history? Secure a brand new, sealed unit from our old stock — with warranty as per OEM standards for the product type.
              </p>

              {/* Side-by-side buttons on mobile using flex-row and flex-1 */}
              <div className="flex flex-row flex-wrap gap-2 lg:gap-3 mb-6 lg:mb-10">
                <a href="#inventory" className="flex-1 sm:flex-none">
                  <Button 
                    className="w-full bg-white text-blue-700 hover:bg-blue-50 font-bold shadow-xl h-10 lg:h-12 px-2 lg:px-8 text-xs lg:text-base"
                  >
                    <ListChecks className="mr-1.5 lg:mr-2" size={16} />
                    View Available Stock
                  </Button>
                </a>
                <Button 
                  variant="outline" 
                  onClick={() => document.getElementById('clearance-diff')?.scrollIntoView({ behavior: 'smooth' })}
                  className="flex-1 sm:flex-none border-white/30 text-white hover:bg-white/10 backdrop-blur-sm h-10 lg:h-12 px-2 lg:px-8 text-xs lg:text-base"
                >
                  Used vs. Sealed
                </Button>
              </div>

              {/* Compact Stats row for mobile */}
              <div className="grid grid-cols-3 gap-2 lg:gap-4 max-w-md">
                {[
                  { value: '100%', label: 'Sealed Boxes' },
                  { value: 'OEM', label: 'Std. Warranty' },
                  { value: 'Brand', label: 'New Inside' },
                ].map((s) => (
                  <div key={s.label} className="text-center bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg lg:rounded-xl py-2 lg:py-3 px-1 shadow-inner">
                    <div className="text-lg lg:text-3xl font-bold text-white">{s.value}</div>
                    <div className="text-[9px] lg:text-xs text-blue-200 font-semibold mt-0.5 lg:mt-1 uppercase tracking-wider">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Empty right column to let the image breathe */}
            <div className="hidden lg:block lg:col-span-2"></div>
          </div>
        </div>
      </section>

      {/* ─── WHY IT IS CHEAP SECTION ─── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 lg:-mt-12 relative z-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
          {clearanceReasons.map((item, index) => (
            <Card key={index} className="border-none shadow-md lg:shadow-xl bg-white rounded-xl lg:rounded-2xl hover:-translate-y-1 transition-transform duration-300">
              <CardContent className="p-4 lg:p-6 text-center lg:text-left">
                <div className="w-8 h-8 lg:w-12 lg:h-12 bg-blue-50 border border-blue-100 rounded-lg lg:rounded-xl flex items-center justify-center mx-auto lg:mx-0 mb-3 lg:mb-4">
                  <ShieldCheck className="text-blue-600 w-4 h-4 lg:w-6 lg:h-6" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm lg:text-lg mb-1 lg:mb-2">{item.title}</h3>
                <p className="text-[10px] lg:text-sm text-gray-600 leading-snug lg:leading-relaxed font-medium">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ─── THE DIFFERENCE SECTION ─── */}
      <section id="clearance-diff" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="text-center mb-8 lg:mb-12">
          <span className="text-blue-600 font-bold text-xs lg:text-sm uppercase tracking-wide">The Smart Investment</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mt-2">Why Buy Old Stock Instead of Second Hand?</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          
          {/* Used AC Column */}
          <div className="bg-gray-50 rounded-2xl p-6 lg:p-10 border border-gray-200 shadow-inner relative overflow-hidden order-2 md:order-1">
            <div className="absolute top-0 left-0 w-full h-1 bg-gray-300"></div>
            <h3 className="text-xl lg:text-2xl font-bold text-gray-500 flex items-center gap-3 mb-6 lg:mb-8">
              <span className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-white flex items-center justify-center text-gray-400 border border-gray-200 text-sm lg:text-base shadow-sm">✕</span>
              Standard Second Hand AC
            </h3>
            <ul className="space-y-4 lg:space-y-6">
              {['No official company warranty', 'Unknown internal repair history', 'Aged, power-hungry compressors', 'Potential for mold & copper leaks', 'No guaranteed installation backing'].map((text, i) => (
                <li key={i} className="flex gap-3 text-gray-500 font-medium text-sm lg:text-base items-start">
                  <div className="w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-white flex items-center justify-center flex-shrink-0 mt-0.5 border border-gray-200 shadow-sm">
                    <div className="w-2 h-0.5 lg:w-2.5 bg-gray-400"></div>
                  </div>
                  <span className="leading-snug">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Clearance Column */}
          <div className="bg-white rounded-2xl p-6 lg:p-10 border-2 border-blue-100 shadow-xl relative overflow-hidden md:-translate-y-2 transition-transform order-1 md:order-2">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-blue-600"></div>
            <h3 className="text-xl lg:text-2xl font-bold text-blue-700 flex items-center gap-3 mb-6 lg:mb-8">
              <span className="w-8 h-8 lg:w-10 lg:h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100 text-sm lg:text-base shadow-sm">✓</span>
              Sealed Stock
            </h3>
            <ul className="space-y-4 lg:space-y-6">
              {['Warranty as per OEM standards', 'Zero previous owners or usage', 'Brand new, untouched components', '100% factory-clean internal coils', 'Professional installation by our engineers'].map((text, i) => (
                <li key={i} className="flex gap-3 text-gray-900 font-bold text-sm lg:text-base items-start">
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5 w-5 h-5 lg:w-6 lg:h-6" />
                  <span className="leading-snug">{text}</span>
                </li>
              ))}
            </ul>
          </div>

        </div>
      </section>

      {/* ─── CURRENT INVENTORY TABLE SECTION ─── */}
      <section id="inventory" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-24">
        <div className="text-center mb-8 lg:mb-10">
          <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 font-bold text-xs lg:text-sm uppercase tracking-wide px-4 py-2 rounded-full border border-blue-100">
            <ListChecks size={16} /> Live Inventory
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mt-4">Currently Available Stock</h2>
          <p className="text-gray-500 mt-2 text-sm lg:text-base font-medium">Buy online, or tap a unit for its full details.</p>
          <p className="mx-auto mt-2 max-w-2xl text-xs leading-snug text-gray-600 lg:text-sm">{STAR_NOTE_ALL}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-2.5 sm:p-6">
            <div className="mb-5 flex flex-wrap justify-center gap-2">
              {BRANDS.map((b) => {
                // Counts what can still be bought, not sold-out cards.
                const n = CLEARANCE.filter((u) => !u.sold && (b === 'All' || u.brand === b)).length;
                return (
                  <button key={b} type="button" aria-pressed={brand === b} onClick={() => setBrand(b)}
                    className={brand === b
                      ? 'min-h-[44px] rounded-xl border-2 border-blue-600 bg-blue-50 px-4 text-sm font-semibold text-blue-800'
                      : 'min-h-[44px] rounded-xl border border-gray-300 bg-white px-4 text-sm text-gray-700 hover:border-gray-400'}>
                    {b} ({n})
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 lg:gap-5">
              {shown.map((u) => (
                <article key={`${u.model}-${u.year}`} className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left lg:rounded-2xl">
                  {/* Photo, brand logo, stock */}
                  <Link to={`/${u.slug}`} className="relative block aspect-square border-b border-gray-100 bg-white lg:aspect-[4/3]">
                    <img src={u.img} alt={`${u.brand} ${u.title} ${u.model}`} loading="lazy" width="1000" height="750"
                      className={`h-full w-full object-contain p-1.5 lg:p-2.5 ${u.sold ? 'opacity-40' : ''}`} />
                    <span className="absolute left-2 top-2 flex rounded-md border border-gray-100 bg-white px-1.5 py-1 lg:left-3 lg:top-3 lg:rounded-lg lg:px-2 lg:py-1.5">
                      <img src={BRAND_LOGO[u.brand]} alt={u.brand} loading="lazy" className="block h-2 w-auto lg:h-3.5" />
                    </span>
                    <span className={`absolute right-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold lg:right-3 lg:top-3 lg:px-2.5 lg:py-1 lg:text-xs ${
                      u.sold ? 'bg-red-600 text-white' : u.qty <= 1 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'
                    }`}>
                      {u.sold ? 'Sold out' : u.qty <= 1 ? 'Last unit' : `${u.qty} left`}
                    </span>
                  </Link>

                  <div className={`flex flex-grow flex-col gap-0.5 px-2.5 pt-2.5 lg:gap-1 lg:px-4 lg:pt-3.5 ${u.sold ? 'opacity-60' : ''}`}>
                    <h3 className="text-[13px] font-semibold leading-snug text-gray-800 lg:text-base">
                      <Link to={`/${u.slug}`} className="hover:text-blue-700">{u.title}</Link>
                    </h3>
                    <p className="font-mono text-[10px] text-gray-500 lg:text-xs">{u.model}</p>
                    {u.specs && u.specs.length > 0 && (
                      <p className="mt-0.5 text-[11px] text-gray-700 lg:mt-1 lg:text-[13px]">
                        <span className="lg:hidden">{u.specs.slice(0, 2).join(' \u00B7 ')}</span>
                        <span className="hidden lg:inline">{u.specs.join(' \u00B7 ')}</span>
                      </p>
                    )}
                    {u.maxTemp && !u.sold && (
                      <p className="flex items-center gap-1 text-[11px] font-semibold text-orange-700 lg:text-[13px]">
                        <Thermometer className="h-3 w-3 lg:h-3.5 lg:w-3.5" aria-hidden="true" />
                        <span>Cools up to {u.maxTemp}{'\u00B0'}C<span className="hidden lg:inline"> outside</span></span>
                      </p>
                    )}
                    {!u.sold && (
                      <div className="mt-1 flex flex-wrap gap-1 lg:mt-1.5 lg:gap-1.5">
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-700 lg:hidden">{u.year} · Sealed</span>
                        <span className="hidden rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 lg:inline">Sealed pack</span>
                        <span className="hidden rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 lg:inline">{u.year} model</span>
                        {u.hasKit && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-700 lg:px-2.5 lg:py-1 lg:text-xs">Piping kit</span>}
                        {u.feature && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-700 lg:px-2.5 lg:py-1 lg:text-xs">
                            <span className="lg:hidden">{u.featureShort || u.feature}</span>
                            <span className="hidden lg:inline">{u.feature}</span>
                          </span>
                        )}
                      </div>
                    )}
                    <p className="mt-1.5 flex items-baseline gap-1 lg:mt-2.5 lg:gap-1.5">
                      <span className={`text-base font-bold text-gray-900 lg:text-[22px] ${u.sold ? 'line-through' : ''}`}>{'\u20B9'}{u.price.toLocaleString('en-IN')}</span>
                      <span className="text-[10px] text-gray-600 lg:text-xs">incl. GST</span>
                    </p>
                  </div>

                  <div className="p-2.5 lg:px-4 lg:pb-4 lg:pt-3.5">
                    {u.sold ? (
                      <a href={similarLink(u)} target="_blank" rel="noopener noreferrer"
                        onClick={() => track(FUNNEL.WHATSAPP_HANDOFF, { source: 'clearance_sold_out', model: u.model })}
                        className="flex h-[42px] items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-green-700 bg-white text-xs font-semibold text-green-700 hover:bg-green-50 lg:h-[46px] lg:gap-2 lg:rounded-[10px] lg:text-[15px]">
                        <WhatsAppGlyph className="h-[15px] w-[15px] lg:h-[18px] lg:w-[18px]" />
                        <span className="lg:hidden">Ask for similar</span>
                        <span className="hidden lg:inline">Ask for similar stock</span>
                      </a>
                    ) : (
                      <button type="button" onClick={() => buyNow(u)}
                        aria-label={`Buy the ${u.brand} ${u.model} now`}
                        className="flex h-[42px] w-full items-center justify-center rounded-lg bg-blue-700 text-[13px] font-semibold text-white hover:bg-blue-800 lg:h-[46px] lg:rounded-[10px] lg:text-[15px]">
                        Buy now
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
            <p className="mt-5 text-center text-xs leading-relaxed text-gray-600">
              Delivery across Delhi NCR. Installation by our own engineers, surveyed and quoted separately.
            </p>
          </div>
          
          <div className="bg-gray-50/80 border-t border-gray-100 p-4 text-center">
            <p className="text-xs text-gray-500 font-medium flex items-center justify-center gap-1.5">
              <Clock size={14} className="text-blue-600" />
              Stock status changes rapidly. Final availability is confirmed over phone.
            </p>
          </div>
        </div>
      </section>

      {/* ─── BOTTOM CTA STRIP ─── */}
      <section className="bg-gradient-to-r from-blue-700 to-indigo-800 py-12 lg:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 lg:mb-4">Stock Changes Every Day.</h2>
          <p className="text-blue-100 mb-6 lg:mb-8 text-sm lg:text-lg font-medium max-w-2xl mx-auto leading-relaxed">
            Because these are unique sealed-pack clearance items, inventory moves incredibly fast. Call us right now to lock in these prices.
          </p>
          <div className="flex flex-row flex-wrap justify-center gap-2 lg:gap-4">
            <a href="tel:+917703900307" className="flex-1 sm:flex-none">
              <Button
                className="bg-white text-blue-700 hover:bg-blue-50 font-bold px-2 lg:px-8 h-12 lg:h-14 text-xs lg:text-base shadow-xl w-full"
              >
                <Phone className="mr-1.5 lg:mr-2" size={16} />
                Call +91 77039 00307
              </Button>
            </a>
            <a href="https://wa.me/message/X4OR7NTM36W5M1" target="_blank" rel="noopener noreferrer" className="flex-1 sm:flex-none">
              <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 px-2 lg:px-8 h-12 lg:h-14 text-xs lg:text-base font-bold backdrop-blur-sm w-full">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};